import React, { useState, useEffect } from 'react';
import { Search, Save, FileDown, Plus, X, GraduationCap, ArrowRight, BookOpen, Check, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

const KardexConvalidaciones = () => {
    // Main View States
    const [registros, setRegistros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [gestion, setGestion] = useState(new Date().getFullYear().toString());
    const [activeTab, setActiveTab] = useState('Convalidacion'); // 'Convalidacion' | 'Suficiencia'

    // Modal States
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('Convalidacion');
    const [estudiantesCandidatos, setEstudiantesCandidatos] = useState([]);
    const [searchCandidato, setSearchCandidato] = useState('');
    const [selectedEstudiante, setSelectedEstudiante] = useState(null);
    const [asignaturas, setAsignaturas] = useState([]);
    const [convalidacionesForm, setConvalidacionesForm] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [resolucionGlobal, setResolucionGlobal] = useState('');
    const [candidatesLoading, setCandidatesLoading] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => loadRegistros(), 400);
        return () => clearTimeout(timer);
    }, [gestion, search]);

    // Auto-load candidates list every time modal opens
    useEffect(() => {
        if (showModal) {
            setEstudiantesCandidatos([]);
            setSearchCandidato('');
            loadCandidatos('');
        }
    }, [showModal]);

    const loadRegistros = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/kardex/convalidaciones?gestion=${gestion}&q=${search}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setRegistros(data.convalidaciones || []);
        } catch (error) {
            console.error('Error loading convalidaciones:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCandidatos = async (query = '') => {
        setCandidatesLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/kardex/convalidaciones/estudiantes?q=${encodeURIComponent(query)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setEstudiantesCandidatos(data.estudiantes || []);
        } catch (error) {
            console.error('Error loading candidates:', error);
        } finally {
            setCandidatesLoading(false);
        }
    };

    const loadEstudianteData = async (est) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/kardex/convalidaciones/${est.id_estudiante}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (!response.ok) { alert(data.message || 'Error al cargar datos'); return; }

            const asigList = data.asignaturas || [];
            setAsignaturas(asigList);

            // Start with a single blank row
            setConvalidacionesForm([{
                id_asignatura: '',
                nota_final: 100,
                materia_origen: '',
                tipo: modalType === 'Suficiencia' ? 'Suficiencia' : 'Convalidacion',
                activo: true
            }]);

            setResolucionGlobal('');
            setSelectedEstudiante(est);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddRow = () => {
        setConvalidacionesForm([...convalidacionesForm, {
            id_asignatura: '',
            nota_final: 100,
            materia_origen: '',
            tipo: modalType === 'Suficiencia' ? 'Suficiencia' : 'Convalidacion',
            activo: true
        }]);
    };

    const handleRemoveRow = (index) => {
        if (convalidacionesForm.length === 1) return;
        const next = [...convalidacionesForm];
        next.splice(index, 1);
        setConvalidacionesForm(next);
    };

    const handleFormChange = (index, field, value) => {
        const next = [...convalidacionesForm];
        next[index][field] = value;
        setConvalidacionesForm(next);
    };

    const toggleActivo = (index) => {
        const next = [...convalidacionesForm];
        next[index].activo = !next[index].activo;
        setConvalidacionesForm(next);
    };

    const handleSave = async () => {
        const payload = convalidacionesForm
            .filter(c => c.activo && c.id_asignatura !== '')
            .map(c => ({
                id_asignatura: parseInt(c.id_asignatura),
                nota_final: parseFloat(c.nota_final) || 100,
                materia_origen: c.materia_origen || '',
                tipo: modalType === 'Suficiencia' ? 'Suficiencia' : 'Convalidacion',
                resolucion: resolucionGlobal || null,
                observaciones: null
            }));

        if (payload.length === 0) {
            alert('Añada al menos una materia válida');
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/kardex/convalidaciones`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    id_estudiante: selectedEstudiante.id_estudiante,
                    gestion: parseInt(gestion),
                    convalidaciones: payload
                })
            });
            const result = await response.json();
            if (response.ok) {
                setShowModal(false);
                setSelectedEstudiante(null);
                loadRegistros();
                alert(`${modalType === 'Suficiencia' ? 'Suficiencia' : 'Convalidaciones'} guardadas exitosamente`);
            } else {
                alert(result.message || 'Error al guardar');
            }
        } catch (error) {
            console.error('Error saving:', error);
            alert('Error de conexión');
        } finally {
            setSubmitting(false);
        }
    };

    const downloadExcel = () => {
        const isSuficiencia = activeTab === 'Suficiencia';
        const headers = isSuficiencia
            ? ["Código", "Nombres", "Apellidos", "CI", "Carrera", "Materia", "Código Materia", "Tipo", "Nota Final", "Resolución", "Fecha"]
            : ["Código", "Nombres", "Apellidos", "CI", "Carrera", "Materia Origen", "Materia Destino", "Código Materia", "Tipo", "Nota Final", "Resolución", "Fecha"];

        const data = displayRegistros.map(r => {
            const row = [
                r.codigo_estudiante, r.nombres, r.apellidos, r.cedula_identidad, r.carrera_nombre
            ];
            if (!isSuficiencia) {
                row.push(r.materia_origen || '');
            }
            row.push(
                r.asignatura_nombre, r.asignatura_codigo, r.tipo, r.nota_final, r.resolucion || '',
                r.fecha_convalidacion ? new Date(r.fecha_convalidacion).toLocaleDateString() : ''
            );
            return row;
        });
        const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Convalidaciones");
        const anchor = document.createElement('a');
        const blob = new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' });
        anchor.href = URL.createObjectURL(blob);
        anchor.download = `Reporte_Convalidaciones_${gestion}.xlsx`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(anchor.href);
    };

    const getStatusStyle = (tipo) => {
        switch (tipo) {
            case 'Homologacion': return 'bg-amber-100 text-amber-700 border-amber-200';
            case 'Suficiencia': return 'bg-purple-100 text-purple-700 border-purple-200';
            default: return 'bg-blue-100 text-blue-700 border-blue-200';
        }
    };

    const displayRegistros = registros.filter(r => activeTab === 'Suficiencia' ? r.tipo === 'Suficiencia' : r.tipo !== 'Suficiencia');

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="mb-6">
                    <h1 className="text-xl font-bold text-gray-800">Kardex &gt; Convalidaciones</h1>
                </div>

                {/* <div className="flex flex-wrap items-center gap-4">
                    
                    <div className="flex items-center bg-white p-1 rounded-xl border border-slate-200 shadow-sm hover:border-slate-300 transition-all group">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-blue-500 transition-colors" size={15} />
                            <input type="text" placeholder="Buscar registro..." value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="bg-transparent border-none pl-9 pr-4 py-1.5 text-xs font-bold w-44 focus:ring-0 outline-none placeholder:text-slate-300 text-slate-600" />
                        </div>
                    </div>

                   
                    <div className="flex items-center bg-white p-1 rounded-xl border border-slate-200 shadow-sm hover:border-slate-300 transition-all">
                        <select value={gestion} onChange={(e) => setGestion(e.target.value)}
                            className="bg-transparent border-none px-4 py-1.5 rounded-xl text-xs font-bold text-slate-500 focus:ring-0 cursor-pointer">
                            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>

                    
                    <button onClick={downloadExcel} className="bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-black px-5 py-3 rounded-xl transition-all flex items-center gap-2 shadow-md shadow-emerald-50 active:scale-95 border-b-2 border-emerald-700">
                        <FileDown size={14} />
                    </button>

                   
                    <div className="flex gap-2">
                        {activeTab === 'Suficiencia' ? (
                            <button onClick={() => { setModalType('Suficiencia'); setShowModal(true); loadCandidatos(); setSelectedEstudiante(null); setSearchCandidato(''); }}
                                className="bg-purple-700 hover:bg-purple-800 text-white text-[10px] font-black px-6 py-3 rounded-xl transition-all flex items-center gap-2 shadow-lg shadow-purple-50 active:scale-95 border-b-2 border-purple-900">
                                <Plus size={16} />
                                AÑADIR SUFICIENCIA
                            </button>
                        ) : (
                            <button onClick={() => { setModalType('Convalidacion'); setShowModal(true); loadCandidatos(); setSelectedEstudiante(null); setSearchCandidato(''); }}
                                className="bg-blue-900 hover:bg-blue-800 text-white text-[10px] font-black px-6 py-3 rounded-xl transition-all flex items-center gap-2 shadow-lg shadow-blue-50 active:scale-95 border-b-2 border-blue-950">
                                <Plus size={16} />
                                AÑADIR CONVALIDACIÓN
                            </button>
                        )}
                    </div>
                </div> */}

                <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex flex-wrap gap-4 items-end">
                        {/* Buscar y Descargar */}
                        <div className="flex-grow flex items-end gap-2">
                            <div className="relative flex-grow">
                                {/* <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label> */}
                                <div className="relative">
                                    <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                    <input
                                        type="text"
                                        placeholder="Buscar: nombre, código, docente, sala, paralelo..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                    />
                                </div>
                            </div>

                        </div>
                        {/* Gestión */}
                        <div className="relative">
                            {/* <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label> */}
                            <select
                                value={gestion} onChange={(e) => setGestion(e.target.value)}
                                className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                            >
                                {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                            <span className="absolute right-3 top-[12px] pointer-events-none text-gray-400 text-xs">▼</span>
                        </div>
                        <button
                            onClick={downloadExcel}
                            className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                            title="Descargar Excel"
                        >
                            <Download size={22} />
                        </button>

                        {/* New Buttons */}
                        <div className="flex gap-2">
                            {activeTab === 'Suficiencia' ? (
                                <button onClick={() => { setModalType('Suficiencia'); setShowModal(true); loadCandidatos(); setSelectedEstudiante(null); setSearchCandidato(''); }}
                                    className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800">
                                    <Plus size={18} />
                                    Añadir Suficiencia
                                </button>
                            ) : (
                                <button onClick={() => { setModalType('Convalidacion'); setShowModal(true); loadCandidatos(); setSelectedEstudiante(null); setSearchCandidato(''); }}
                                    className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800">
                                    <Plus size={18} />
                                    Añadir Convalidación
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="mb-4 flex gap-2">
                <button
                    onClick={() => setActiveTab('Convalidacion')}
                    className={`px-6 py-2.5 rounded-full font-medium transition ${activeTab === 'Convalidacion' ? 'bg-blue-900 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                    Convalidaciones
                </button>
                <button
                    onClick={() => setActiveTab('Suficiencia')}
                    className={`px-6 py-2.5 rounded-full font-medium transition ${activeTab === 'Suficiencia' ? 'bg-blue-900 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                    Pruebas de Suficiencia
                </button>
            </div>


            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">Código / Estudiante</th>
                                <th className="px-3 py-3 text-center font-semibold border">CI</th>
                                <th className="px-3 py-3 text-center font-semibold border">Carrera</th>
                                {activeTab !== 'Suficiencia' && (
                                    <th className="px-3 py-3 text-center font-semibold border">Materia Origen</th>
                                )}
                                <th className="px-3 py-3 text-center font-semibold border">
                                    {activeTab === 'Suficiencia' ? 'Materia' : 'Materia Destino'}
                                </th>
                                <th className="px-3 py-3 text-center font-semibold border">Tipo</th>
                                <th className="px-3 py-3 text-center font-semibold border">Nota</th>
                                <th className="px-3 py-3 text-center font-semibold border">Resolución</th>
                                <th className="px-3 py-3 text-center font-semibold border">Fecha</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array(3).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan={8} className="px-6 py-5"><div className="h-6 bg-slate-50 rounded-lg w-full"></div></td>
                                    </tr>
                                ))
                            ) : displayRegistros.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-20 text-center text-slate-400 font-medium italic">
                                        No se encontraron registros de {activeTab === 'Suficiencia' ? 'pruebas de suficiencia' : 'convalidaciones'} para la gestión {gestion}.
                                    </td>
                                </tr>
                            ) : (
                                displayRegistros.map((reg) => (
                                    <tr key={reg.id_convalidacion} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 border">
                                            <div className="text-[10px] ">{reg.codigo_estudiante}</div>
                                            <div className="font-bold">{reg.apellidos}, {reg.nombres}</div>
                                        </td>
                                        <td className="px-3 py-2 border text-center">{reg.cedula_identidad}</td>
                                        <td className="px-3 py-2 border text-center">{reg.carrera_nombre}</td>
                                        {activeTab !== 'Suficiencia' && (
                                            <td className="px-3 py-2 border text-center">{reg.materia_origen || '—'}</td>
                                        )}
                                        <td className="px-3 py-2 border">
                                            <div className="font-bold text-blue-700 text-xs">{reg.asignatura_nombre}</div>
                                            <div className="text-[9px]">{reg.asignatura_codigo}</div>
                                        </td>
                                        <td className="px-3 py-2 border text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(reg.tipo)}`}>
                                                {reg.tipo || 'Convalidacion'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 border text-center">{reg.nota_final}</td>
                                        <td className="px-3 py-2 border text-center">{reg.resolucion || '—'}</td>
                                        <td className="px-3 py-2 border text-center">
                                            {reg.fecha_convalidacion ? new Date(reg.fecha_convalidacion).toLocaleDateString() : '---'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    {/* <div className="absolute inset-0 bg-black/50" onClick={() => { setShowModal(false); setSelectedEstudiante(null); }}></div> */}
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        {/* Header */}
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                                {/* <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><BookOpen size={20} /></div> */}
                                <h2 className="text-xl font-bold text-blue-900">
                                    {modalType === 'Suficiencia' ? 'Nueva Prueba de Suficiencia' : 'Nueva Convalidación'}
                                </h2>
                           
                            <button onClick={() => { setShowModal(false); setSelectedEstudiante(null); }} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
                        </div>

                        {/* Body */}
                        <div className="p-8 overflow-y-auto">
                            {!selectedEstudiante ? (
                                <div className="space-y-5">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-bold  uppercase">Seleccionar Estudiante</p>
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                                            <input type="text" placeholder="Nombre o CI..." value={searchCandidato}
                                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                                onChange={(e) => { setSearchCandidato(e.target.value); loadCandidatos(e.target.value); }} />
                                        </div>
                                    </div>

                                    {candidatesLoading ? (
                                        <div className="flex items-center justify-center py-12 gap-3">
                                            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                            <span className="text-slate-400 text-sm">Buscando estudiantes...</span>
                                        </div>
                                    ) : estudiantesCandidatos.length === 0 ? (
                                        <div className="text-center py-12 text-slate-400 text-sm italic">
                                            No se encontraron estudiantes matriculados.
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {estudiantesCandidatos.map(est => (
                                                <button key={est.id_estudiante} onClick={() => loadEstudianteData(est)}
                                                    className="flex items-center gap-3 p-3 bg-white border border-slate-100 rounded-2xl hover:border-blue-400 hover:bg-blue-50/50 transition-all text-left">
                                                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs shrink-0">
                                                        {est.nombres?.[0] || '?'}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-slate-700 text-xs">{est.apellido_paterno}, {est.nombres}</p>
                                                        <p className="text-[9px] text-slate-400 font-mono">{est.codigo} · {est.carrera_nombre}</p>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-5">
                                    {/* Student bar */}
                                    <div className="p-4 bg-slate-50 rounded-2xl flex items-center justify-between border border-slate-100">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-blue-900 flex items-center justify-center text-white font-bold">
                                                {selectedEstudiante.nombres?.[0]}
                                            </div>
                                            <div>
                                                <p className="font-bold text-slate-800">{selectedEstudiante.apellido_paterno}, {selectedEstudiante.nombres}</p>
                                                <p className="text-[10px] font-medium text-slate-400">CI: {selectedEstudiante.cedula_identidad} · {selectedEstudiante.carrera_nombre}</p>
                                            </div>
                                        </div>
                                        <button onClick={() => setSelectedEstudiante(null)} className="text-slate-400 hover:text-slate-600 p-1">
                                            <X size={16} />
                                        </button>
                                    </div>

                                    {/* Global resolution */}
                                    <div className="flex items-center gap-3">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Nro. Resolución:</label>
                                        <input type="text" value={resolucionGlobal} onChange={(e) => setResolucionGlobal(e.target.value)}
                                            placeholder="Ej: RES-2026-001"
                                            className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-bold focus:outline-none focus:border-blue-300" />
                                    </div>

                                    {/* Subject list */}
                                    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-50 border-b border-slate-100">
                                                <tr>
                                                    <th className="px-4 py-3 text-[9px] font-bold text-slate-400 uppercase tracking-widest text-left w-2/5">
                                                        {modalType === 'Suficiencia' ? 'Materia' : 'Materia Destino'}
                                                    </th>
                                                    {modalType !== 'Suficiencia' && (
                                                        <th className="px-4 py-3 text-[9px] font-bold text-slate-400 uppercase tracking-widest text-left w-1/4">
                                                            Materia Origen
                                                        </th>
                                                    )}
                                                    <th className="px-4 py-3 text-[9px] font-bold text-slate-400 uppercase tracking-widest text-center w-24">Nota</th>
                                                    <th className="px-4 py-3 text-[9px] font-bold text-slate-400 uppercase tracking-widest text-center w-12"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-50">
                                                {convalidacionesForm.map((conv, idx) => (
                                                    <tr key={idx} className="bg-white hover:bg-slate-50/50 transition-colors">
                                                        <td className="px-4 py-3">
                                                            <select
                                                                value={conv.id_asignatura}
                                                                onChange={(e) => handleFormChange(idx, 'id_asignatura', e.target.value)}
                                                                className="w-full p-2 text-xs font-bold text-slate-700 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-400 focus:bg-white"
                                                            >
                                                                <option value="">-- Seleccionar Materia --</option>
                                                                {Array.from(new Set(asignaturas.map(a => a.nivel || 'General'))).sort((a, b) => {
                                                                    if (a === 'General') return 1;
                                                                    if (b === 'General') return -1;
                                                                    return String(a).localeCompare(String(b), undefined, { numeric: true });
                                                                }).map(nivel => (
                                                                    <optgroup key={`niv-${nivel}`} label={nivel === 'General' ? 'General / Sin Año' : `Nivel ${nivel} / Año ${nivel}`}>
                                                                        {asignaturas.filter(a => (a.nivel || 'General') === nivel).map(asig => (
                                                                            <option key={asig.id_asignatura} value={asig.id_asignatura}>
                                                                                {asig.nombre} ({asig.codigo})
                                                                            </option>
                                                                        ))}
                                                                    </optgroup>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        {modalType !== 'Suficiencia' && (
                                                            <td className="px-4 py-3">
                                                                <input type="text" value={conv.materia_origen}
                                                                    onChange={(e) => handleFormChange(idx, 'materia_origen', e.target.value)}
                                                                    placeholder="Ej: Álgebra I..."
                                                                    className="w-full p-2 text-xs font-medium bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-400 focus:bg-white" />
                                                            </td>
                                                        )}
                                                        <td className="px-4 py-3 text-center">
                                                            <input type="number" min="1" max="100" value={conv.nota_final}
                                                                onChange={(e) => handleFormChange(idx, 'nota_final', e.target.value)}
                                                                className="w-16 p-2 text-center text-xs font-black text-slate-700 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-400 focus:bg-white mx-auto block" />
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <button onClick={() => handleRemoveRow(idx)}
                                                                className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                                                                title="Eliminar fila">
                                                                <X size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <div className="p-3 bg-slate-50 border-t border-slate-100 flex justify-center">
                                            <button onClick={handleAddRow}
                                                className="flex items-center gap-2 text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-100/50 hover:bg-blue-100 px-4 py-2 rounded-lg transition-all">
                                                <Plus size={14} /> AÑADIR OTRA MATERIA
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {selectedEstudiante && (
                            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
                                <p className="text-xs text-slate-400">
                                    {convalidacionesForm.filter(c => c.activo).length} materia(s) seleccionada(s)
                                </p>
                                <button onClick={handleSave} disabled={submitting}
                                    className={`px-10 py-2.5 rounded-full font-bold text-xs text-white shadow-lg transition-all flex items-center gap-2 active:scale-95 ${submitting ? 'bg-slate-400' : 'bg-blue-900 hover:bg-blue-800'}`}>
                                    {submitting && <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                                    <span>{submitting ? 'GUARDANDO...' : 'GUARDAR'}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default KardexConvalidaciones;
